import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';

const ImageCarousel = ({
  images = [],
  className = '',
  aspectRatio = 'aspect-[16/10]',
  rounded = 'rounded-lg',
  placeholder = null, // no placeholder → empty/failed states show a "no photos yet" prompt
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [failedUrls, setFailedUrls] = useState(new Set());

  useEffect(() => {
    if (images.length > 0 && currentIndex >= images.length) {
      setCurrentIndex(images.length - 1);
    }
  }, [images.length, currentIndex]);

  const hasImages = images.length > 0;
  const showControls = images.length > 1;

  const goTo = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
  }, [goPrev, goNext]);

  const handleImageError = useCallback((imageId) => {
    setFailedUrls(prev => new Set(prev).add(imageId));
  }, []);

  const getImageSrc = (image) => {
    // The API `url` field is authoritative (presigned R2 URL or null). Never
    // fabricate a /static/uploads/<domain>/<filename> path — for R2-backed
    // images that file does not exist on disk and 404s in production. Fall
    // back to the caller-provided placeholder instead (see CLAUDE.md
    // "Schema-Authoritative URLs").
    if (failedUrls.has(image.id) || !image.url) return placeholder;
    return image.url;
  };

  // Shown when there are no images (or an image URL failed) and the caller
  // supplied no placeholder. Prompts the user to add a photo instead of
  // rendering a stock image that may not match the content (see the Paint
  // "kitchen placeholder" bug this replaced).
  const emptyContent = (
    <div className="flex flex-col items-center justify-center text-center px-4 w-full h-full">
      <ImageOff size={28} className="text-[var(--text-muted)] mb-2" />
      <p className="text-sm font-medium text-[var(--text-secondary)]">No photos yet</p>
      <p className="text-xs text-[var(--text-muted)] mt-0.5">Add a photo to get started</p>
    </div>
  );

  // Shown for a single slide whose image URL is missing or failed to load,
  // when no placeholder is set. Distinct from emptyContent: other slides may
  // still have valid photos, so "No photos yet" would be wrong here.
  const failedContent = (
    <div className="flex flex-col items-center justify-center text-center px-4 w-full h-full">
      <ImageOff size={28} className="text-[var(--text-muted)] mb-2" />
      <p className="text-sm font-medium text-[var(--text-secondary)]">Image unavailable</p>
    </div>
  );

  if (!hasImages) {
    return (
      <div className={`${aspectRatio} ${rounded} bg-[var(--bg-hover)] overflow-hidden ${className}`}>
        {placeholder ? (
          <img
            src={placeholder}
            alt="No image available"
            className="w-full h-full object-cover"
          />
        ) : (
          emptyContent
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative ${aspectRatio} ${rounded} overflow-hidden group ${className}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Image carousel"
    >
      {/* Slide track */}
      <div
        className="flex h-full transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((image) => {
          const src = getImageSrc(image); // may be null when the URL failed and no placeholder is set
          return (
            <div key={image.id} className="w-full h-full flex-shrink-0 relative bg-[var(--bg-hover)]">
              {src ? (
                <img
                  src={src}
                  alt={image.original_filename || 'Image'}
                  className="w-full h-full object-cover"
                  onError={() => handleImageError(image.id)}
                />
              ) : (
                failedContent
              )}
              {image.is_primary && (
                <span className="absolute top-3 left-3 bg-[var(--primary)] text-[var(--primary-text)] text-xs font-medium px-2 py-0.5 rounded shadow">
                  Primary
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation arrows */}
      {showControls && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-all backdrop-blur-sm"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-all backdrop-blur-sm"
            aria-label="Next image"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {showControls && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-4'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image counter */}
      {showControls && (
        <div className="absolute top-3 right-3 bg-black/40 text-white text-xs font-medium px-2 py-1 rounded backdrop-blur-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
